import type { BikeModel, StationWithDistance } from '@/domain/geo';

/* The two models Vammo rents today. Everything else the station record still lists —
   Tinbot TS1, VMOTO VS1/VS2/F01/NEW TS — is retired stock that would only add noise to a
   page whose whole job is "can I swap here for my bike". */
export type SupportedModel = 'comfort' | 'cpx';

/* Coverage is what the map paints: one colour per station, so the three states have to be
   mutually exclusive. */
export type Coverage = SupportedModel | 'both';

/* Keyed by id, not by name: the stored names drift ("VMOTO CPX" in the payload,
   "I/VMOTO SUPER SOCO CPX" on the document) while the id is what the station record
   actually joins on.

   Comfort carries two ids because the model was seeded separately per environment — 18 in
   production, 45 in dev. Listing both keeps dev honest; keying on production's id alone
   made every dev station look like CPX-only, which is a silent wrong answer rather than a
   visible break. */
const MODEL_BY_ID = new Map<number, SupportedModel>([
  [7, 'cpx'],
  [18, 'comfort'],
  [45, 'comfort'],
]);

/* Comfort first, then CPX — a fixed order so chips and legend never reshuffle between
   stations. */
const ORDER: readonly SupportedModel[] = ['comfort', 'cpx'];

const LABEL = {
  comfort: 'Comfort',
  cpx: 'CPX',
} satisfies Record<SupportedModel, string>;

/* Design-system tokens from index.css: yellow-700 "Dark Warning", blue-500 "Neutral",
   neon-pink. Not derived at runtime — the legend has to name the same three colours the
   map paints, so they are pinned in one place.

   Both models is yellow because it used to be green, and green beside the CPX pink is the
   pair red-green colour blindness collapses. It is also the majority of the map — 165 of
   287 stations — so that pair is most of what a lead has to tell apart.

   yellow-700 and not the brighter yellow-500. Sampled from a CARTO voyager tile of the
   served area, yellow-500 reads at 1.32:1 against the dominant basemap colour, which is
   below the green it replaces (2.00) and the faintest dot on the map — a colour-blindness
   fix that costs every other lead the dot. yellow-700 reads at 2.91:1, the strongest of
   the three, and separates from blue and pink under simulated protanopia, deuteranopia
   and tritanopia just as well.

   Still open, and not this change: blue against pink is the worst pair left. It measures
   34 units apart under protanopia where green against pink measured 139. Comfort-only and
   CPX-only are a quarter of the network each and both carry a written label, so the map is
   readable — but the pair to fix next is that one, not this one. */
const COVERAGE_COLOR = {
  both: '#998a00',
  comfort: '#0ba4f9',
  cpx: '#ff6ed8',
} satisfies Record<Coverage, string>;

/* A station listing only retired models tells us nothing about the two we rent, and
   hiding it would be worse than showing it — it is a real station with real cabinets. So
   assume it serves both rather than inventing a restriction the lead would plan around.
   Same for an empty list, which is what a payload without the relation looks like. */
/* Only what the station actually lists. No fallback: a caller that would rather say
   nothing than guess needs to see the empty answer. */
const listedModels = (models: readonly BikeModel[]): SupportedModel[] => {
  const known = new Set<SupportedModel>();
  for (const model of models) {
    const supported = MODEL_BY_ID.get(model.id);
    if (supported) {
      known.add(supported);
    }
  }
  return ORDER.filter((model) => known.has(model));
};

export const supportedModels = (models: readonly BikeModel[]): SupportedModel[] => {
  const listed = listedModels(models);
  return listed.length === 0 ? [...ORDER] : listed;
};

export const coverageOf = (models: readonly BikeModel[]): Coverage => {
  const supported = supportedModels(models);
  return supported.length === ORDER.length ? 'both' : (supported[0] ?? 'both');
};

export const coverageColor = (coverage: Coverage): string => COVERAGE_COLOR[coverage];

export const modelLabel = (model: SupportedModel): string => LABEL[model];

/* How far the lead is from a station that takes their own bike. One model can be five
   minutes away while the other is on the far side of the network, and the band alone
   cannot say so: it reads the closest station of any kind. Comfort first, then CPX, and a
   model no station serves is left out rather than reported as unreachable.

   No ordering is assumed of the list. Callers rank it, nothing in the type says so, and a
   wrong answer here invents a station the lead does not have.

   A station listing no model we rent counts for neither. The station tag assumes both
   there, because going silent about a real station reads as "no bikes here", but reach
   cannot borrow that assumption: it would invent a station for a model. `bike_models`
   parses with `.catch([])`, so a dropped relation would otherwise mark every band with
   both models and look exactly like the common case. No mark says "we do not know". */
export type ModelDistance = { model: SupportedModel; distanceKm: number };

export const nearestByModel = (
  stations: readonly StationWithDistance[],
): ModelDistance[] => {
  const closest = new Map<SupportedModel, number>();
  for (const station of stations) {
    for (const model of listedModels(station.bikeModels)) {
      const known = closest.get(model);
      if (known === undefined || station.distanceKm < known) {
        closest.set(model, station.distanceKm);
      }
    }
  }
  return ORDER.flatMap((model) => {
    const distanceKm = closest.get(model);
    return distanceKm === undefined ? [] : [{ model, distanceKm }];
  });
};
